import { useCallback, useRef, useState } from "react";
import { Alert, Button, IconButton, Stack, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import type { Channel } from "stream-chat";
import { useChatContext } from "stream-chat-react";
import { Resources, useResource } from "Translations/Resources";
import { useAmount } from "Hooks/useAmount";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import { AmountField } from "Components/Chat/AmountField";
import { BottomDrawer } from "Components/Chat/BottomDrawer";
import { buildPaymentMessageText } from "Components/Chat/chatAttachmentTypes";
import { identityUserPublicIdFromStreamId } from "Components/Chat/chatConstants";

const RESOURCES = Resources.Chat.MultiPayerRequest.Drawer;
const VALIDATION_RESOURCES = Resources.Chat.PaymentRequest.Drawer.Validation;

const parseAmount = (raw: string): number => Number(raw.replace(",", "."));

const getSchema = (t: (key: string) => string) =>
  z.object({
    // Raw typed string, parsed for validation — same shape as PaymentDrawerForm.
    amount: z
      .string()
      .refine((v) => v.trim().length > 0, t(VALIDATION_RESOURCES.EnterAmount))
      .refine((v) => {
        const parsed = parseAmount(v);
        return !Number.isNaN(parsed) && parsed > 0;
      }, t(VALIDATION_RESOURCES.AmountMustBePositive)),
  });

type MultiPayerRequestFormData = z.infer<ReturnType<typeof getSchema>>;

type Props = {
  isOpen: boolean;
  onClose: () => void;
  channel: Channel;
};

/**
 * Posts a multi-payer payment request: a fixed amount in the author's market currency that any
 * member may pay (any number of times), each payment being a direct payment to the author's
 * primary account posted as a thread reply. Nothing is created server-side here — the message
 * IS the request (see MultiPayerPaymentRequestAttachment), which is why there is no bank
 * account picker: the receiving account is resolved at pay time.
 */
export const CreateMultiPayerRequestDrawer = ({
  isOpen,
  onClose,
  channel,
}: Props) => {
  const { t } = useResource();
  const toAmountWithCurrency = useAmount();
  // Chat gates on IdentityUser + verified phone, NOT on region — and useAmount throws for a
  // region-less session. Surface an error instead of crashing the drawer.
  const isRegionMissing = !useLoggedInUser()?.region;
  const {
    client: { userID },
  } = useChatContext();

  const { control, handleSubmit, reset, formState } =
    useForm<MultiPayerRequestFormData>({
      resolver: zodResolver(getSchema(t)),
      defaultValues: { amount: "" },
    });

  const amountInputRef = useRef<HTMLInputElement | null>(null);
  const focusAmountField = useCallback(() => {
    amountInputRef.current?.focus();
  }, []);

  const [isSendFailed, setIsSendFailed] = useState(false);

  const handleFormSubmit = async ({ amount }: MultiPayerRequestFormData) => {
    if (isRegionMissing || !userID) {
      return;
    }
    const { value, currencyCode } = toAmountWithCurrency(parseAmount(amount));
    try {
      await channel.sendMessage({
        text: buildPaymentMessageText(t, {
          type: "payment_request_multi",
          amount: value,
          currencyCode,
        }),
        attachments: [
          {
            type: "payment_request_multi",
            amount: value,
            currencyCode,
            recipientIdentityUserPublicID:
              identityUserPublicIdFromStreamId(userID),
          },
        ],
      });
    } catch {
      setIsSendFailed(true);
      return;
    }
    onClose();
  };

  const handleClosed = () => {
    reset();
    setIsSendFailed(false);
  };

  return (
    <BottomDrawer
      isOpen={isOpen}
      onClose={onClose}
      onTransitionEnd={focusAmountField}
      onClosed={handleClosed}
    >
      <Stack gap={2}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h6" fontWeight={600}>
            {t(RESOURCES.Title)}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <Stack gap={3}>
            <AmountField
              control={control}
              name="amount"
              label={t(RESOURCES.Amount)}
              inputRef={amountInputRef}
            />
            <Typography variant="body2" color="text.secondary">
              {t(RESOURCES.Hint)}
            </Typography>
            {(isSendFailed || isRegionMissing) && (
              <Alert severity="error">{t(RESOURCES.Error)}</Alert>
            )}
            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={formState.isSubmitting || !formState.isValid}
              sx={{
                "&.Mui-disabled": {
                  color: "white",
                },
              }}
            >
              {t(RESOURCES.Submit)}
            </Button>
          </Stack>
        </form>
      </Stack>
    </BottomDrawer>
  );
};
